/**
 * NCALayer Service
 * 
 * Сервис для взаимодействия с NCALayer через WebSocket.
 * NCALayer - клиентское приложение для работы с ЭЦП РК.
 * 
 * Документация: https://pki.gov.kz/ncalayer/
 */

const NCALAYER_URL = 'wss://127.0.0.1:13579/'
const NCALAYER_COMMONUTILS_MODULE = 'kz.gov.pki.knca.commonUtils'
const NCALAYER_BASICS_MODULE = 'kz.gov.pki.knca.basics'
const CONNECT_TIMEOUT_MS = 5000
const DEFAULT_REQUEST_TIMEOUT_MS = 120000
const SIGN_REQUEST_TIMEOUT_MS = 300000

class NCALayerService {
    constructor() {
        this.webSocket = null
        this.callback = null
        this.connectionPromise = null
        this.isConnected = false
    }

    _normalizeResponse(result) {
        if (!result || typeof result !== 'object') {
            return result
        }

        if (result.body && typeof result.body === 'object' && !Array.isArray(result.body)) {
            return {
                ...result,
                ...result.body,
            }
        }

        return result
    }

    _extractResponseObject(result) {
        const normalized = this._normalizeResponse(result)

        if (!normalized || typeof normalized !== 'object') {
            return normalized
        }

        if (Object.prototype.hasOwnProperty.call(normalized, 'responseObject')) {
            return normalized.responseObject
        }

        if (Object.prototype.hasOwnProperty.call(normalized, 'result')) {
            return normalized.result
        }

        if (Object.prototype.hasOwnProperty.call(normalized, 'body')) {
            return normalized.body
        }

        return normalized
    }

    _unwrapResponseObject(result) {
        let current = result
        const visited = new Set()

        while (current && typeof current === 'object' && !Array.isArray(current) && !visited.has(current)) {
            visited.add(current)

            if (Object.prototype.hasOwnProperty.call(current, 'responseObject')) {
                current = current.responseObject
                continue
            }

            if (Object.prototype.hasOwnProperty.call(current, 'result')) {
                current = current.result
                continue
            }

            if (Object.prototype.hasOwnProperty.call(current, 'body')) {
                current = current.body
                continue
            }

            break
        }

        return current
    }

    _extractTokenList(result) {
        const responseObject = this._unwrapResponseObject(this._extractResponseObject(result))
        const tokens = this._normalizeTokenList(responseObject)

        return [...new Set(['PKCS12', ...tokens])]
    }

    _normalizeTokenList(value) {
        if (Array.isArray(value)) {
            return value
                .map((token) => typeof token === 'string' ? token.trim() : '')
                .filter(Boolean)
        }

        if (typeof value === 'string') {
            const trimmedValue = value.trim()

            if (!trimmedValue) {
                return []
            }

            if (
                (trimmedValue.startsWith('[') && trimmedValue.endsWith(']'))
                || (trimmedValue.startsWith('{') && trimmedValue.endsWith('}'))
            ) {
                try {
                    return this._normalizeTokenList(JSON.parse(trimmedValue))
                } catch (error) {
                    console.warn('NCALayer: Failed to parse token list JSON', error)
                }
            }

            return [trimmedValue]
        }

        if (!value || typeof value !== 'object') {
            return []
        }

        const candidateKeys = [
            'tokens',
            'storageTypes',
            'storages',
            'activeTokens',
            'availableStorages',
            'storageNames',
            'storageList',
        ]

        for (const key of candidateKeys) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
                const normalizedTokens = this._normalizeTokenList(value[key])
                if (normalizedTokens.length) {
                    return normalizedTokens
                }
            }
        }

        return []
    }

    _extractSignatureValue(result) {
        const responseObject = this._unwrapResponseObject(this._extractResponseObject(result))

        if (typeof responseObject === 'string') {
            return responseObject
        }

        if (Array.isArray(responseObject)) {
            return responseObject[0] || ''
        }

        if (responseObject && typeof responseObject === 'object') {
            if (Array.isArray(responseObject.signatures)) {
                return responseObject.signatures[0] || ''
            }

            if (typeof responseObject.signatures === 'string') {
                return responseObject.signatures
            }
        }

        return ''
    }

    _isTerminalResponse(result) {
        const normalized = this._normalizeResponse(result)

        if (!normalized || typeof normalized !== 'object') {
            return false
        }

        if (Object.prototype.hasOwnProperty.call(normalized, 'status')) {
            return true
        }

        if (Object.prototype.hasOwnProperty.call(normalized, 'code')) {
            return true
        }

        if (Object.prototype.hasOwnProperty.call(normalized, 'result')) {
            return true
        }

        if (Object.prototype.hasOwnProperty.call(normalized, 'responseObject')) {
            return true
        }

        if (Object.prototype.hasOwnProperty.call(normalized, 'success')) {
            return true
        }

        if (Object.prototype.hasOwnProperty.call(normalized, 'errorCode')) {
            return true
        }

        if (Object.prototype.hasOwnProperty.call(normalized, 'body')) {
            return true
        }

        return false
    }

    /**
     * Подключение к NCALayer
     * @returns {Promise<boolean>} - успешность подключения
     */
    async connect() {
        if (this.isConnected && this.webSocket?.readyState === WebSocket.OPEN) {
            return true
        }

        if (this.connectionPromise) {
            return this.connectionPromise
        }

        this.connectionPromise = new Promise((resolve, reject) => {
            try {
                this.webSocket = new WebSocket(NCALAYER_URL)

                this.webSocket.onopen = () => {
                    console.log('NCALayer: Connection opened')
                    this.isConnected = true
                    resolve(true)
                }

                this.webSocket.onclose = (event) => {
                    console.log('NCALayer: Connection closed', event.code, event.reason)
                    this.isConnected = false
                    this.connectionPromise = null
                    if (!event.wasClean) {
                        reject(new Error('NCALayer не запущен или недоступен'))
                    }
                }

                this.webSocket.onerror = (error) => {
                    console.error('NCALayer: WebSocket error', error)
                    this.isConnected = false
                    this.connectionPromise = null
                    reject(new Error('Ошибка подключения к NCALayer'))
                }

                this.webSocket.onmessage = (event) => {
                    try {
                        const result = JSON.parse(event.data)
                        console.debug('NCALayer: Incoming message', result)
                        if (this.callback) {
                            this.callback(result)
                        }
                    } catch (e) {
                        console.error('NCALayer: Error parsing response', e)
                    }
                }

                // Таймаут подключения
                setTimeout(() => {
                    if (!this.isConnected) {
                        this.webSocket?.close()
                        reject(new Error('Таймаут подключения к NCALayer'))
                    }
                }, CONNECT_TIMEOUT_MS)

            } catch (error) {
                reject(new Error('Не удалось создать подключение к NCALayer'))
            }
        })

        return this.connectionPromise
    }

    /**
     * Отправка сообщения в NCALayer
     * @param {Object} message - сообщение для отправки
     * @returns {Promise<Object>} - ответ от NCALayer
     */
    _send(message, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS) {
        return new Promise((resolve, reject) => {
            if (!this.webSocket || this.webSocket.readyState !== WebSocket.OPEN) {
                reject(new Error('NCALayer не подключен'))
                return
            }

            if (this.callback) {
                reject(new Error('NCALayer уже обрабатывает другой запрос'))
                return
            }

            const timeout = setTimeout(() => {
                this.callback = null
                reject(new Error('Таймаут ожидания ответа от NCALayer'))
            }, timeoutMs)

            this.callback = (result) => {
                const normalized = this._normalizeResponse(result)

                if (!this._isTerminalResponse(normalized)) {
                    return
                }

                clearTimeout(timeout)
                this.callback = null

                const code = Object.prototype.hasOwnProperty.call(normalized, 'code')
                    ? String(normalized.code)
                    : null

                if (normalized.errorCode) {
                    reject(new Error(normalized.message || normalized.details || normalized.errorCode))
                } else if (code && code !== '200') {
                    reject(new Error(normalized.message || 'Ошибка NCALayer'))
                } else if (Object.prototype.hasOwnProperty.call(normalized, 'status') && !normalized.status) {
                    reject(new Error(normalized.message || normalized.details || 'Ошибка NCALayer'))
                } else if (Object.prototype.hasOwnProperty.call(normalized, 'success') && !normalized.success) {
                    reject(new Error(normalized.message || normalized.details || 'Ошибка NCALayer'))
                } else {
                    resolve(normalized)
                }
            }

            this.webSocket.send(JSON.stringify(message))
        })
    }

    /**
     * Проверка доступности NCALayer
     * @returns {Promise<boolean>}
     */
    async isNCALayerRunning() {
        try {
            await this.connect()
            return true
        } catch (error) {
            return false
        }
    }

    /**
     * Получение списка активных токенов (носителей ключей)
     * @returns {Promise<string[]>} - список доступных носителей
     */
    async getActiveTokens() {
        await this.connect()

        const result = await this._send({
            module: NCALAYER_COMMONUTILS_MODULE,
            method: 'getActiveTokens'
        })

        return this._extractTokenList(result)
    }

    /**
     * Получение информации о ключе
     * @param {string} storageName - имя носителя (PKCS12 или токен)
     * @returns {Promise<Object>} - информация о ключе
     */
    async getKeyInfo(storageName = 'PKCS12') {
        await this.connect()

        const result = await this._send({
            module: NCALAYER_COMMONUTILS_MODULE,
            method: 'getKeyInfo',
            args: [storageName]
        })

        return this._extractResponseObject(result)
    }

    /**
     * Создание CMS подписи из Base64 данных
     * @param {string} storageName - имя носителя
     * @param {string} base64Data - данные для подписи в base64
     * @param {boolean} includeData - включать ли данные в подпись
     * @returns {Promise<string>} - CMS подпись в base64
     */
    async createCMSSignature(storageName, base64Data, includeData = true) {
        this.disconnect()
        await this.connect()

        try {
            console.debug('NCALayer: Trying basics.sign for CMS signature')
            const basicsResult = await this._send({
                module: NCALAYER_BASICS_MODULE,
                method: 'sign',
                args: {
                    allowedStorages: storageName ? [storageName] : undefined,
                    format: 'cms',
                    data: base64Data,
                    signingParams: {
                        decode: true,
                        encapsulate: includeData,
                        digested: false,
                        tsaProfile: null
                    },
                    signerParams: {
                        extKeyUsageOids: [],
                        iin: '',
                        bin: '',
                        serialNumber: '',
                        chain: null,
                    },
                    locale: 'ru'
                }
            }, SIGN_REQUEST_TIMEOUT_MS)

            const basicsSignature = this._extractSignatureValue(basicsResult)
            if (basicsSignature) {
                return basicsSignature
            }

            console.warn('NCALayer: basics.sign returned empty signature, falling back to commonUtils')
        } catch (error) {
            console.warn('NCALayer: basics.sign failed, falling back to commonUtils', error)
            this.disconnect()
            await this.connect()
        }

        const result = await this._send({
            module: NCALAYER_COMMONUTILS_MODULE,
            method: 'createCMSSignatureFromBase64',
            args: [storageName, 'SIGNATURE', base64Data, includeData]
        }, SIGN_REQUEST_TIMEOUT_MS)

        const signature = this._extractSignatureValue(result)
        if (!signature) {
            throw new Error('NCALayer не вернул подпись после ввода пароля')
        }

        return signature
    }

    /**
     * Подпись XML
     * @param {string} storageName - имя носителя
     * @param {string} xmlToSign - XML для подписи
     * @returns {Promise<string>} - подписанный XML
     */
    async signXml(storageName, xmlToSign) {
        await this.connect()

        const result = await this._send({
            module: NCALAYER_COMMONUTILS_MODULE,
            method: 'signXml',
            args: [storageName, 'SIGNATURE', xmlToSign, '', '']
        })

        return this._extractResponseObject(result)
    }

    /**
     * Изменение языка интерфейса NCALayer
     * @param {string} locale - код языка (kk, ru, en)
     */
    async changeLocale(locale) {
        await this.connect()

        await this._send({
            module: NCALAYER_COMMONUTILS_MODULE,
            method: 'changeLocale',
            args: [locale]
        })
    }

    /**
     * Закрытие соединения
     */
    disconnect() {
        if (this.webSocket) {
            this.webSocket.close()
            this.webSocket = null
            this.isConnected = false
            this.connectionPromise = null
        }
    }

    /**
     * Извлечение ИИН из subjectDn
     * @param {string} subjectDn - DN субъекта из сертификата
     * @returns {string|null} - ИИН или null
     */
    extractIINFromDN(subjectDn) {
        if (!subjectDn) return null

        // Паттерн для ИИН: SERIALNUMBER=IIN123456789012
        const match = subjectDn.match(/SERIALNUMBER=IIN(\d{12})/i)
        if (match) {
            return match[1]
        }

        // Альтернативный формат
        const match2 = subjectDn.match(/2\.5\.4\.5=IIN(\d{12})/i)
        if (match2) {
            return match2[1]
        }

        return null
    }
}

// Экспортируем singleton
export const ncaLayerService = new NCALayerService()
export default ncaLayerService
